import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { MagnifyingGlass, MapPin, CalendarBlank, Users } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import api from "../lib/api";

export default function SearchBar({ variant = "hero" }) {
  const [city, setCity] = useState("");
  const [cities, setCities] = useState([]);
  const [checkIn, setCheckIn] = useState(new Date());
  const [checkOut, setCheckOut] = useState(
    new Date(Date.now() + 24 * 60 * 60 * 1000)
  );
  const [guests, setGuests] = useState(2);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/hotels/cities").then(({ data }) => setCities(data)).catch(() => {});
  }, []);

  const submit = () => {
    const params = new URLSearchParams();
    if (city) params.set("city", city);
    params.set("checkIn", checkIn.toISOString().slice(0, 10));
    params.set("checkOut", checkOut.toISOString().slice(0, 10));
    params.set("guests", String(guests));
    nav(`/hotels?${params.toString()}`);
  };

  const isHero = variant === "hero";

  return (
    <div
      className={`${
        isHero ? "glass" : "bg-white border border-black/10"
      } rounded-2xl p-2 flex flex-col md:flex-row items-stretch gap-2 shadow-lg`}
      data-testid="search-bar"
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="flex-1 flex items-center gap-3 px-5 py-3 rounded-xl hover:bg-stone transition-colors text-left"
            data-testid="search-city-btn"
          >
            <MapPin size={20} weight="duotone" className="text-gold" />
            <div>
              <p className="text-[10px] tracking-widest uppercase text-neutral-500">City</p>
              <p className="text-sm text-midnight font-medium">
                {city || "Anywhere in India"}
              </p>
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56">
          <DropdownMenuItem onClick={() => setCity("")}>Anywhere in India</DropdownMenuItem>
          {cities.map((c) => (
            <DropdownMenuItem key={c} onClick={() => setCity(c)} data-testid={`city-opt-${c}`}>
              {c}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="hidden md:block w-px bg-black/10 my-2" />

      <Popover>
        <PopoverTrigger asChild>
          <button
            className="flex-1 flex items-center gap-3 px-5 py-3 rounded-xl hover:bg-stone transition-colors text-left"
            data-testid="search-checkin-btn"
          >
            <CalendarBlank size={20} weight="duotone" className="text-gold" />
            <div>
              <p className="text-[10px] tracking-widest uppercase text-neutral-500">Check-in</p>
              <p className="text-sm text-midnight font-medium">{format(checkIn, "dd MMM yyyy")}</p>
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={checkIn}
            onSelect={(d) => d && setCheckIn(d)}
            disabled={(d) => d < new Date(new Date().toDateString())}
            initialFocus
          />
        </PopoverContent>
      </Popover>

      <div className="hidden md:block w-px bg-black/10 my-2" />

      <Popover>
        <PopoverTrigger asChild>
          <button
            className="flex-1 flex items-center gap-3 px-5 py-3 rounded-xl hover:bg-stone transition-colors text-left"
            data-testid="search-checkout-btn"
          >
            <CalendarBlank size={20} weight="duotone" className="text-gold" />
            <div>
              <p className="text-[10px] tracking-widest uppercase text-neutral-500">Check-out</p>
              <p className="text-sm text-midnight font-medium">{format(checkOut, "dd MMM yyyy")}</p>
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={checkOut}
            onSelect={(d) => d && setCheckOut(d)}
            disabled={(d) => d <= checkIn}
            initialFocus
          />
        </PopoverContent>
      </Popover>

      <div className="hidden md:block w-px bg-black/10 my-2" />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="flex-1 flex items-center gap-3 px-5 py-3 rounded-xl hover:bg-stone transition-colors text-left"
            data-testid="search-guests-btn"
          >
            <Users size={20} weight="duotone" className="text-gold" />
            <div>
              <p className="text-[10px] tracking-widest uppercase text-neutral-500">Guests</p>
              <p className="text-sm text-midnight font-medium">
                {guests} {guests === 1 ? "guest" : "guests"}
              </p>
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {[1, 2, 3, 4, 5, 6].map((n) => (
            <DropdownMenuItem key={n} onClick={() => setGuests(n)} data-testid={`guest-opt-${n}`}>
              {n} {n === 1 ? "guest" : "guests"}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <button
        onClick={submit}
        className="flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-midnight text-bone hover:bg-midnight-hover transition-colors font-medium tracking-wide"
        data-testid="search-submit-btn"
      >
        <MagnifyingGlass size={18} weight="bold" />
        Search
      </button>
    </div>
  );
}
